// Handyman pricing calculations
// Extracted from RequestWizard.jsx for modular architecture

import {
  HANDYMAN_SERVICE_PRICES,
  SERVICE_TO_FLOW_MAP,
  INTERIOR_REPAIR_SERVICES,
  INTERIOR_REPAIR_QUANTITY,
  RENTAL_TURNOVER_SCOPE_PRICES,
  HANDYMAN_ITEM_COUNT_PRICES,
  WALL_TYPE_MODIFIERS,
  TV_SIZE_MODIFIERS,
  FURNITURE_ANCHORING_MODIFIER,
  DOORS_LOCKS_ITEM_COUNT,
  EXTERIOR_DOOR_MODIFIER,
  FRAMING_MODIFICATION_MODIFIER,
  CARPENTRY_SCOPE_PRICES,
  DRYWALL_FINISHING_MODIFIER,
  PAINTING_INCLUDED_MODIFIER,
  ELECTRICAL_FIXTURE_COUNT,
  CEILING_FIXTURE_MODIFIER,
  EXPOSED_WIRING_MODIFIER,
  PLUMBING_FIXTURE_COUNT,
  ACTIVE_LEAK_MODIFIER
} from './handymanData';

/**
 * Calculate interior repair pricing based on selected service and options
 * @param {Object} answers - All wizard answers
 * @returns {{low: number, high: number}}
 */
export const calculateInteriorRepairPrice = (answers) => {
  const selectedService = answers.interior_repair_services;
  if (!selectedService) return { low: 0, high: 0 };
  
  const service = INTERIOR_REPAIR_SERVICES[selectedService];
  if (!service) return { low: 0, high: 0 };
  
  // Handle variable quantity services
  if (service.isVariable) {
    if (selectedService === 'hang_blinds') {
      const qty = answers.blinds_quantity || '1';
      const multiplier = INTERIOR_REPAIR_QUANTITY[qty] || 1;
      return {
        low: Math.round(service.low * multiplier),
        high: Math.round(service.high * multiplier)
      };
    } else if (selectedService === 'replace_outlets') {
      const qty = answers.outlets_quantity || '1';
      const multiplier = INTERIOR_REPAIR_QUANTITY[qty] || 1;
      return {
        low: Math.round(service.low * multiplier),
        high: Math.round(service.high * multiplier)
      };
    } else if (selectedService === 'rental_turnover') {
      const scope = answers.rental_turnover_scope || 'light';
      const scopePrice = RENTAL_TURNOVER_SCOPE_PRICES[scope] || RENTAL_TURNOVER_SCOPE_PRICES.light;
      return { low: scopePrice.low, high: scopePrice.high };
    }
  }
  
  // Fixed price service
  return { low: service.low, high: service.high };
};

/**
 * Calculate mounting/assembly pricing
 * @param {Object} answers - All wizard answers
 * @returns {{low: number, high: number}}
 */
export const calculateMountingPrice = (answers) => {
  let totalLow = 0;
  let totalHigh = 0;
  
  // Base pricing from item count
  const itemCount = answers.mount_item_count;
  if (itemCount && HANDYMAN_ITEM_COUNT_PRICES[itemCount]) {
    const countRange = HANDYMAN_ITEM_COUNT_PRICES[itemCount];
    totalLow += countRange.low;
    totalHigh += countRange.high;
  }
  
  // Add wall type modifiers
  const wallType = answers.wall_type;
  if (wallType && WALL_TYPE_MODIFIERS[wallType]) {
    const wallMod = WALL_TYPE_MODIFIERS[wallType];
    totalLow += wallMod.low;
    totalHigh += wallMod.high;
  }
  
  // Add TV size modifier (if TV selected)
  const mountItems = answers.mount_items || [];
  if (mountItems.includes('tv_mount') && answers.tv_size) {
    const tvMod = TV_SIZE_MODIFIERS[answers.tv_size];
    if (tvMod) {
      totalLow += tvMod.low;
      totalHigh += tvMod.high;
    }
  }
  
  // Add furniture anchoring modifier
  if (mountItems.includes('furniture') && answers.furniture_anchoring === 'yes') {
    totalLow += FURNITURE_ANCHORING_MODIFIER.low;
    totalHigh += FURNITURE_ANCHORING_MODIFIER.high;
  }
  
  return { low: totalLow, high: totalHigh };
};

/**
 * Calculate doors & locks pricing
 * @param {Object} answers - All wizard answers
 * @returns {{low: number, high: number}}
 */
export const calculateDoorsLocksPrice = (answers) => {
  let totalLow = 0;
  let totalHigh = 0;
  
  const doorCount = answers.door_lock_count;
  if (doorCount && DOORS_LOCKS_ITEM_COUNT[doorCount]) {
    const countRange = DOORS_LOCKS_ITEM_COUNT[doorCount];
    totalLow += countRange.low;
    totalHigh += countRange.high;
  }
  
  // Exterior door modifier
  if (answers.is_exterior_door === 'yes') {
    totalLow += EXTERIOR_DOOR_MODIFIER.low;
    totalHigh += EXTERIOR_DOOR_MODIFIER.high;
  }
  
  // Framing modification modifier
  if (answers.framing_modification === 'yes') {
    totalLow += FRAMING_MODIFICATION_MODIFIER.low;
    totalHigh += FRAMING_MODIFICATION_MODIFIER.high;
  }
  
  return { low: totalLow, high: totalHigh };
};

/**
 * Calculate carpentry pricing
 * @param {Object} answers - All wizard answers
 * @returns {{low: number, high: number}}
 */
export const calculateCarpentryPrice = (answers) => {
  let totalLow = 0;
  let totalHigh = 0;
  
  const carpentryScope = answers.carpentry_scope;
  if (carpentryScope && CARPENTRY_SCOPE_PRICES[carpentryScope]) {
    const scopeRange = CARPENTRY_SCOPE_PRICES[carpentryScope];
    totalLow += scopeRange.low;
    totalHigh += scopeRange.high;
  }
  
  // Drywall finishing modifier
  if (answers.drywall_finishing === 'yes') {
    totalLow += DRYWALL_FINISHING_MODIFIER.low;
    totalHigh += DRYWALL_FINISHING_MODIFIER.high;
  }
  
  // Painting included modifier
  if (answers.painting_included === 'yes') {
    totalLow += PAINTING_INCLUDED_MODIFIER.low;
    totalHigh += PAINTING_INCLUDED_MODIFIER.high;
  }
  
  return { low: totalLow, high: totalHigh };
};

/**
 * Calculate small electrical pricing
 * @param {Object} answers - All wizard answers
 * @returns {{low: number, high: number}}
 */
export const calculateSmallElectricalPrice = (answers) => {
  let totalLow = 0;
  let totalHigh = 0;
  
  const fixtureCount = answers.electrical_fixture_count;
  if (fixtureCount && ELECTRICAL_FIXTURE_COUNT[fixtureCount]) {
    const countRange = ELECTRICAL_FIXTURE_COUNT[fixtureCount];
    totalLow += countRange.low;
    totalHigh += countRange.high;
  }
  
  // Ceiling fixture modifier
  if (answers.is_ceiling_fixture === 'yes') {
    totalLow += CEILING_FIXTURE_MODIFIER.low;
    totalHigh += CEILING_FIXTURE_MODIFIER.high;
  }
  
  // Exposed wiring modifier
  if (answers.exposed_wiring === 'yes') {
    totalLow += EXPOSED_WIRING_MODIFIER.low;
    totalHigh += EXPOSED_WIRING_MODIFIER.high;
  }
  
  return { low: totalLow, high: totalHigh };
};

/**
 * Calculate small plumbing pricing
 * @param {Object} answers - All wizard answers
 * @returns {{low: number, high: number}}
 */
export const calculateSmallPlumbingPrice = (answers) => {
  let totalLow = 0;
  let totalHigh = 0;
  
  const plumbingCount = answers.plumbing_fixture_count;
  if (plumbingCount && PLUMBING_FIXTURE_COUNT[plumbingCount]) {
    const countRange = PLUMBING_FIXTURE_COUNT[plumbingCount];
    totalLow += countRange.low;
    totalHigh += countRange.high;
  }
  
  // Active leak modifier
  if (answers.active_leak === 'yes') {
    totalLow += ACTIVE_LEAK_MODIFIER.low;
    totalHigh += ACTIVE_LEAK_MODIFIER.high;
  }
  
  return { low: totalLow, high: totalHigh };
};

/**
 * Get triggered flows from selected services
 * @param {Array<string>} selectedServices - List of selected service values
 * @returns {Set<string>} - Set of triggered flow names
 */
export const getTriggeredFlows = (selectedServices) => {
  const triggeredFlows = new Set();
  (selectedServices || []).forEach(serviceValue => {
    const flowTrigger = SERVICE_TO_FLOW_MAP[serviceValue];
    if (flowTrigger) {
      triggeredFlows.add(flowTrigger);
    }
  });
  return triggeredFlows;
};

/**
 * Calculate complete handyman estimate
 * @param {Object} answers - All wizard answers
 * @returns {{low: number, high: number, hasValidPrice: boolean}}
 */
export const calculateHandymanEstimate = (answers) => {
  let totalLow = 0;
  let totalHigh = 0;
  let hasValidPrice = false;
  
  const handymanServices = answers.handyman_services || [];
  const triggeredFlows = getTriggeredFlows(handymanServices);
  const effectiveServices = [...handymanServices, ...Array.from(triggeredFlows)];
  
  // Track which flows have been priced via their detailed calculators
  const pricedFlows = new Set();
  
  // Interior Repair pricing
  if (effectiveServices.includes('interior_repair')) {
    const interiorPrice = calculateInteriorRepairPrice(answers);
    if (interiorPrice.low > 0 || interiorPrice.high > 0) {
      totalLow += interiorPrice.low;
      totalHigh += interiorPrice.high;
      hasValidPrice = true;
      pricedFlows.add('interior_repair');
    }
  }
  
  // Mounting pricing
  if (effectiveServices.includes('hanging_mounting')) {
    const mountingPrice = calculateMountingPrice(answers);
    if (mountingPrice.low > 0 || mountingPrice.high > 0) {
      totalLow += mountingPrice.low;
      totalHigh += mountingPrice.high;
      hasValidPrice = true;
      pricedFlows.add('hanging_mounting');
    }
  }
  
  // Doors & Locks pricing
  if (effectiveServices.includes('doors_locks')) {
    const doorsPrice = calculateDoorsLocksPrice(answers);
    if (doorsPrice.low > 0 || doorsPrice.high > 0) {
      totalLow += doorsPrice.low;
      totalHigh += doorsPrice.high;
      hasValidPrice = true;
      pricedFlows.add('doors_locks');
    }
  }
  
  // Carpentry pricing
  if (effectiveServices.includes('carpentry')) {
    const carpentryPrice = calculateCarpentryPrice(answers);
    if (carpentryPrice.low > 0 || carpentryPrice.high > 0) {
      totalLow += carpentryPrice.low;
      totalHigh += carpentryPrice.high;
      hasValidPrice = true;
      pricedFlows.add('carpentry');
    }
  }
  
  // Small Electrical pricing
  if (effectiveServices.includes('small_electrical')) {
    const electricalPrice = calculateSmallElectricalPrice(answers);
    if (electricalPrice.low > 0 || electricalPrice.high > 0) {
      totalLow += electricalPrice.low;
      totalHigh += electricalPrice.high;
      hasValidPrice = true;
      pricedFlows.add('small_electrical');
    }
  }
  
  // Small Plumbing pricing
  if (effectiveServices.includes('small_plumbing')) {
    const plumbingPrice = calculateSmallPlumbingPrice(answers);
    if (plumbingPrice.low > 0 || plumbingPrice.high > 0) {
      totalLow += plumbingPrice.low;
      totalHigh += plumbingPrice.high;
      hasValidPrice = true;
      pricedFlows.add('small_plumbing');
    }
  }
  
  // Base pricing for all services: 
  // - Services WITHOUT detailed flows: always use base price
  // - Services WITH detailed flows: use base price as FALLBACK if flow hasn't been priced yet
  handymanServices.forEach(serviceValue => {
    const servicePrice = HANDYMAN_SERVICE_PRICES[serviceValue];
    if (!servicePrice) return;
    
    const triggerFlow = SERVICE_TO_FLOW_MAP[serviceValue];
    
    if (!triggerFlow) {
      // No subflow - always add base price
      totalLow += servicePrice.low;
      totalHigh += servicePrice.high;
      hasValidPrice = true;
    } else if (!pricedFlows.has(triggerFlow)) {
      // Has subflow but flow hasn't been priced yet - use base price as fallback
      // This shows an initial estimate before user answers subflow questions
      totalLow += servicePrice.low;
      totalHigh += servicePrice.high;
      hasValidPrice = true;
    }
    // If flow HAS been priced, skip base price to avoid double-counting
  });
  
  return {
    low: totalLow,
    high: totalHigh,
    hasValidPrice
  };
};

export default calculateHandymanEstimate;
